import { message } from 'antd';

import * as Sentry from '@sentry/react';
import { Client, QuestionnaireRequestParams, SignupRequestParams } from '../../libs/types/src';

import { StorageService } from '../../services';
import { GoogleSignInRequest } from '../../libs/ui/src/components/signInForm/GoogleSignIn';
import { ClientMaintenanceService, ClientService } from '../../services';
import { setAuthToken } from '../../services/apiService';
import { open as openWebsocket } from '../../services/sockets';
import { campaignsActions } from '../campaigns';
import { chargebeeActions } from '../chargebee';
import { inboxActions } from '../inbox';
import { instantlyActions } from '../instantly';
import { membersActions } from '../members';
import { notificationsActions } from '../notifications';
import { proxyActions } from '../proxy';
import { rb2bActions } from '../rb2b';
import { usersActions } from '../users';
import {AppDispatch} from '../index';

export const LOGIN_REQUEST = 'LOGIN_REQUEST';
export const LOGIN_SUCCESS = 'LOGIN_SUCCESS';
export const LOGIN_FAILURE = 'LOGIN_FAILURE';
export const SIGNUP_REQUEST = 'SIGNUP_REQUEST';
export const SIGNUP_SUCCESS = 'SIGNUP_SUCCESS';
export const QUESTIONNAIRE_REQUEST = 'QUESTIONNAIRE_REQUEST';
export const QUESTIONNAIRE_SUCCESS = 'QUESTIONNAIRE_SUCCESS';
export const QUESTIONNAIRE_SKIP = 'QUESTIONNAIRE_SKIP';
export const QUESTIONNAIRE_FAILURE = 'QUESTIONNAIRE_FAILURE';
export const SIGNUP_FAILURE = 'SIGNUP_FAILURE';
export const LOGOUT = 'LOGOUT';
export const UPDATE_CLIENT = 'UPDATE_CLIENT';
export const UPDATE_CLIENT_REQUEST = 'UPDATE_CLIENT_REQUEST';
export const UPDATE_CLIENT_SUCCESS = 'UPDATE_CLIENT_SUCCESS';
export const UPDATE_CLIENT_FAILURE = 'UPDATE_CLIENT_FAILURE';
export const UPDATE_CLIENT_TAGS = 'UPDATE_CLIENT_TAGS';
export const CLEAR_STATUS = 'CLEAR_STATUS';
export const GET_CLIENT_REQUEST = 'GET_CLIENT_REQUEST';
export const GET_CLIENT_SUCCESS = 'GET_CLIENT_SUCCESS';
export const INIT_SUCCESS = 'INIT_SUCCESS';
export const GET_CLIENT_FAILURE = 'GET_CLIENT_FAILURE';
export const UPDATE_DATA_CREDITS = 'UPDATE_DATA_CREDITS';
export const UPDATE_CLIENT_SERVER_STATE = 'UPDATE_CLIENT_SERVER_STATE';
export const UPDATE_CLIENT_API_SETUP_STATE = 'UPDATE_CLIENT_API_SETUP_STATE';

export const clientActions = {
  login,
  signup,
  onboardQuestionnaire,
  onboardQuestionnaireSkip,
  logout,
  forceLogout,
  updateAttribute,
  clearClientStatus,
  getClient,
  changePassword,
  requestPasswordReset,
  checkPasswordResetToken,
  updateClientIfNeeded,
  updatePassword,
  changeClientFeatures,
  toggleNoVNCCustomAddressBar,
  updateClientData,
  getClientAndInitialData,
  startCloudInstance,
  getClientServerState,
  generateApiToken,
  getApiToken,
  authorizeClient,
  googleSignIn,
};

function login(email: string, password: string) {
  return async (dispatch: any) => {
    dispatch(request({email, password}));

    return ClientService.login(email, password).then(
      ({client, team_member}) => {
        dispatch(success(client, client.intercom_disabled, team_member));
        dispatch(getInitialClientData(client));
        openWebsocket();
        return client;
      },
      () => dispatch(failure())
    );
  };

  function request(client: any) {
    return {type: LOGIN_REQUEST, client};
  }
  function success(client: any, isAdmin: boolean, team_member: any) {
    return {type: LOGIN_SUCCESS, client, isAdmin, team_member};
  }
  function failure() {
    return {type: LOGIN_FAILURE};
  }
}

function clearClientStatus() {
  return {type: CLEAR_STATUS};
}

function signup(params: SignupRequestParams) {
  function request() {
    return {type: SIGNUP_REQUEST};
  }
  function success(client: any, team_member: any) {
    return {type: SIGNUP_SUCCESS, client, team_member};
  }

  return (dispatch: any) => {
    dispatch(request());
    const utm_params = StorageService.getItem('utm_params');

    return ClientService.signup({
      ...params,
      ...(utm_params && {utm_params}),
    }).then(
      ({client, team_member}: any) => {
        StorageService.removeItem('utm_params');
        dispatch(success(client, team_member));
        return true;
      },
    );
  };
}

function onboardQuestionnaire(params: QuestionnaireRequestParams) {
  function questionnaireRequest() {
    return {type: QUESTIONNAIRE_REQUEST};
  }
  function questionnaireSuccess(client: any) {
    return {type: QUESTIONNAIRE_SUCCESS, client};
  }
  function questionnaireFailure(error: any) {
    return {type: QUESTIONNAIRE_FAILURE, error};
  }

  return (dispatch: any, getState) => {
    const state = getState();
    const clientData = state.client.data_step_1;

    dispatch(questionnaireRequest());

    return ClientService.onboardQuestionnaire(params).then(
      () => {
        dispatch(questionnaireSuccess(clientData));
        dispatch(getInitialClientData(clientData));
        openWebsocket();
      },
      (error: any) => {
        dispatch(questionnaireFailure(error));
      }
    );
  };
}

function onboardQuestionnaireSkip() {
  function questionnaireSkip(client: any) {
    return {type: QUESTIONNAIRE_SKIP, client};
  }

  return (dispatch: any, getState) => {
    const state = getState();
    const clientData = state.client.data_step_1;

    dispatch(questionnaireSkip(clientData));
    dispatch(getInitialClientData(clientData));
    openWebsocket();
  };
}

function forceLogout() {
  return {type: LOGOUT};
}

function logout(unauthorizedError = false) {
  return (dispatch: any, getState: any) => {
    if (getState().client.data) {
      setAuthToken(null);
      ClientService.logout();
    }
    if (process.env.NX_PUBLIC_SENTRY === 'enabled') {
      Sentry.setUser(null);
    }
    dispatch({type: LOGOUT, unauthorizedError});
  };
}

function getClient() {
  return (dispatch: any) => {
    dispatch(request());

    ClientService.getClient().then(
      ({client, team_member}) => {
        dispatch(success(client, team_member));
      },
      () => dispatch(failure())
    );
  };

  function request() {
    return {type: GET_CLIENT_REQUEST};
  }
  function success(client: any, team_member) {
    return {type: GET_CLIENT_SUCCESS, client, team_member};
  }
  function failure() {
    return {type: GET_CLIENT_FAILURE};
  }
}

function getClientAndInitialData() {
  return (dispatch: any) => {
    dispatch(request());

    ClientService.getClient().then(
      ({client, team_member}) => {
        dispatch({type: INIT_SUCCESS, client, isAdmin: client.intercom_disabled});
        dispatch(success(client, team_member));
        dispatch(getInitialClientData(client));
      },
      () => dispatch(failure())
    );
  };

  function request() {
    return {type: GET_CLIENT_REQUEST};
  }
  function success(client: any, team_member) {
    return {type: GET_CLIENT_SUCCESS, client, team_member};
  }
  function failure() {
    return {type: GET_CLIENT_FAILURE};
  }
}

function getInitialClientData(client) {
  return (dispatch: any) => {
    if (process.env.NX_PUBLIC_SENTRY === 'enabled') {
      Sentry.setUser({email: client.email, id: client.id});
    }
    setAuthToken(client.jwt?.access_token);
    dispatch(chargebeeActions.setChargebeeInstance(client));
    dispatch(usersActions.getUsers(client.id)).then(() => dispatch(campaignsActions.getCampaigns(client.id)));
    dispatch(membersActions.getMembers());
    dispatch(membersActions.getCsmList());
    dispatch(instantlyActions.verifyToken());
    dispatch(rb2bActions.getLatestEvent());
    dispatch(getClientServerState());
    dispatch(proxyActions.getCountryList());
    dispatch(proxyActions.getClientCountry());
    if (client.inbox_feature) {
      dispatch(inboxActions.getInboxUnreadCountByUser());
    }
    dispatch(notificationsActions.getNotificationsList());
  };
}

function updateClientIfNeeded() {
  return (dispatch: any, getState: any) => {
    if (getState().client.data) {
      dispatch(getClient());
      dispatch(getClientServerState());
    }
  };
}

function getClientServerState() {
  return (dispatch: any) => {
    ClientMaintenanceService.getServerState()
      .then(res => {
        dispatch({type: UPDATE_CLIENT_SERVER_STATE, state: res?.data?.state});
      })
      .catch(err => {
        console.error('error: ', err);
      });
  };
}

function updateAttribute(attrs: Partial<Client>) {
  return (dispatch: AppDispatch) => {
    ClientService.updateAttribute(attrs).then((client: Client) => {
      dispatch({type: UPDATE_CLIENT, client});
    });
  };
}

function changePassword(token: string, password: string) {
  return async () => {
    return ClientService.changePassword(token, password).then(
      () => {
        message.info('Password has been successfully changed', 10);
        return true;
      },
      () => {
        message.error('Something went wrong. Please, try again later', 10);
        return false;
      }
    );
  };
}

function requestPasswordReset(email: string) {
  return async () => {
    return ClientService.requestPasswordReset(email).then(
      () => {
        message.info('Link with instructions will be sent to your email', 10);
        return true;
      },
      () => {
        message.error('Something went wrong. Please, try again later', 10);
        return false;
      }
    );
  };
}

function checkPasswordResetToken(token: string) {
  return async () => {
    return ClientService.checkPasswordResetToken(token).then(
      (result: any) => {
        const {status} = result;
        return status === 200;
      },
      () => {
        return false;
      }
    );
  };
}

function updatePassword(old_password: string, new_password: string) {
  return (dispatch: any) => {
    dispatch(request());

    return ClientService.updatePassword(old_password, new_password).then(
      () => {
        message.success('Password has been successfully changed', 5);
        dispatch(success());
      },
      ({response}) => {
        dispatch(failure(response.data?.old_password?.[0]?.message || 'Something went wrong. Please, try again later'));
      }
    );
  };

  function request() {
    return {type: UPDATE_CLIENT_REQUEST};
  }
  function success() {
    return {type: UPDATE_CLIENT_SUCCESS};
  }
  function failure(error) {
    return {type: UPDATE_CLIENT_FAILURE, error};
  }
}

function changeClientFeatures(features) {
  return (dispatch: any) => {
    ClientService.changeClientFeatures(features)
      .then(() => {
        dispatch({type: UPDATE_CLIENT_SUCCESS, client: {...features}});
        message.success('Client features changed.');
      })
      .catch(() => {
        message.error('Bad request. Please, try again later.');
      });
  };
}

function toggleNoVNCCustomAddressBar(userId?: number) {
  return () => {
    ClientService.notifyInstance({data_action: 'toggle_custom_address_bar', user_id: userId});
  };
}

function generateApiToken() {
  return async dispatch => {
    const result = ClientService.generateApiToken();
    dispatch({type: UPDATE_CLIENT_API_SETUP_STATE, state: true});
    return result;
  };
}

function getApiToken() {
  return () => {
    return ClientService.getApiToken();
  };
}

function authorizeClient(params) {
  return () => {
    return ClientService.authorizeClient(params);
  };
}

function updateClientData(client: Client) {
  return (dispatch: any) => {
    dispatch({type: UPDATE_CLIENT_SUCCESS, client});
  };
}

function startCloudInstance() {
  return () => {
    ClientMaintenanceService.startCloudInstance();
  };
}

function googleSignIn(requestData: GoogleSignInRequest) {
  return () => {
    return ClientService.googleSignIn(requestData);
  };
}
